<template>
    <LimitedWidthLayout>
        <Typography
            is="h1"
            size="s"
            type="header"
            responsive
            :class="$style.title"
        >
            {{ $tr('contactUs.title') }}
        </Typography>

        <ContentSection is="section">
            <Socials />
        </ContentSection>
        <ContentSection is="section">
            <div :class="$style.ctaContainer">
                <Typography
                    is="h2"
                    size="m"
                >
                    {{ $tr('contactUs.cta') }}
                </Typography>
                <Typography
                    is="p"
                    size="m"
                    :class="$style.ctaSubtitle"
                >
                    {{ $tr('contactUs.ctaSubtitle') }}
                </Typography>
            </div>
            <ContactForm
                v-model="formData"
                :submitStatus="submitStatus"
                @formReset="resetForm"
                @turnstileReady="setTurnstileToken"
                @submit="handleSubmit"
            />
        </ContentSection>
    </LimitedWidthLayout>
</template>
<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import ContactForm from '@/components/ContactForm/ContactForm.vue'
import { useContactForm } from '@/components/ContactForm/use-contact-form'
import Typography from '@/components/Typography.vue'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import LimitedWidthLayout from '@/layouts/LimitedWidthLayout.vue'

import ContentSection from './components/ContentSection.vue'
import Socials from './components/Socials.vue'

const tr = useTr()

const { Organization, WebSite, getFullLink, getInLanguage } = useSchemaOrg()
const route = useRoute()

const organizationSchema = computed(() => {
    return {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
                Organization.value,
                WebSite.value,
                {
                    '@type': 'ContactPage',
                    url: getFullLink(route.path),
                    name: tr('contactUs.metaTitle'),
                    description: tr('contactUs.metaDescription'),
                    inLanguage: getInLanguage(),
                },
            ],
        }),
    }
})

const pageMeta = computed(() => ({
    title: tr('contactUs.metaTitle'),
    meta: [
        {
            name: 'description',
            content: tr('contactUs.metaDescription'),
        },
    ],
    script: [organizationSchema.value],
}))

useHead(pageMeta)

const { formData, submitStatus, resetForm, handleSubmit, setTurnstileToken } = useContactForm()
</script>

<style module>
.ctaContainer {
    display: flex;
    flex-direction: column;
    gap: var(--gap-12);
    margin-bottom: var(--gap-24);
}

.ctaSubtitle {
    color: var(--white-60);
}
.title {
    padding-top: 24px;
}
</style>
